import React, { useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import { accountabilityAPI } from '@/services/accountabilityAPI';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import { Body } from '@/components/Typography';

type ViolationActionModalProps = {
  type: 'uphold' | 'clear';
  violationId: number;
  workerName: string;
  onClose: () => void;
  onSuccess: () => void;
};

const ViolationActionModal = ({
  type,
  violationId,
  workerName,
  onClose,
  onSuccess,
}: ViolationActionModalProps) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) return;

    setLoading(true);
    setError(null);

    try {
      if (type === 'clear') {
        await accountabilityAPI.clearViolation(violationId, reason);
      } else {
        await accountabilityAPI.upholdViolation(violationId, reason);
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : `Failed to ${type} violation`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={type === 'clear' ? 'Clear Violation' : 'Uphold Violation'}
      onRequestClose={onClose}
    >
      <Card.Section>
        <Body>
          {type === 'clear' ? (
            <>
              Clear violation for <strong>{workerName}</strong>? This will
              restore GravyWorker&apos;s profile to its previous status.
            </>
          ) : (
            <>
              Keep violation for <strong>{workerName}</strong>? Their profile
              will stay suspended.
            </>
          )}
        </Body>
      </Card.Section>
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon={faExclamationTriangle}
            status="danger"
            title="Error"
          />
        )}
        <FormElement
          label={
            type === 'clear' ? 'Reason for clearing' : 'Reason for upholding'
          }
        >
          <TextArea
            placeholder="Share your reason before proceeding."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel={type === 'clear' ? 'Clear' : 'Proceed'}
            isLoading={loading}
            label={type === 'clear' ? 'Clear' : 'Proceed'}
            status={type === 'clear' ? 'success' : 'theme'}
            onClick={handleSubmit}
            disabled={!reason.trim()}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default ViolationActionModal;
